import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { IScenario } from 'commonTypes/api/scenarios/scenario';

import { fetchOffersDataApi } from 'shared/api/offers';
import { getStatusesAPI } from 'shared/api/statuses';

import { goToNextScenarioThunk, startScenarioThunk } from 'entities/scenario';

import {
  setMplOffersStatusesData,
  setMplOfferStatus,
  setMplOffersAlreadySentFromSameBank,
  setMplOfferStatusesFromRankingOffers,
} from './reducers';
import type { CreditFullDealScreen } from './types';

export interface ScenarioState {
  currentStep: IScenario | null;
  isLoading: boolean;
  isError: boolean;
  displayedScreen: CreditFullDealScreen | null;
  currentStepOpenedAt: number | null;
}

const initialState: ScenarioState = {
  currentStep: null,
  isLoading: false,
  isError: false,
  displayedScreen: null,
  currentStepOpenedAt: null,
};

export const scenarioSlice = createSlice({
  name: 'scenario',
  initialState,
  reducers: {
    setScenario: (state, action: PayloadAction<IScenario>) => {
      state.currentStep = action.payload;
      state.isLoading = false;
      state.isError = false;
      state.currentStepOpenedAt = Date.now();
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<boolean>) => {
      state.isError = action.payload;
    },
    setDisplayedScreen: (state, action: PayloadAction<CreditFullDealScreen>) => {
      state.displayedScreen = action.payload;
    },
    clearScenario: (state) => {
      state.currentStep = null;
      state.isLoading = false;
      state.isError = false;
      state.currentStepOpenedAt = null;
    },
    currentStepClosed: (state) => {
      state.currentStepOpenedAt = null;
    },
    setMplPreApprovalOfferStatus: setMplOfferStatus,
  },
  extraReducers: (builder) => {
    builder.addCase(goToNextScenarioThunk.fulfilled, setMplOffersAlreadySentFromSameBank);
    builder.addCase(startScenarioThunk.fulfilled, setMplOffersAlreadySentFromSameBank);
    builder.addMatcher(getStatusesAPI.endpoints.getStatuses.matchFulfilled, setMplOffersStatusesData);
    builder.addMatcher(fetchOffersDataApi.endpoints.getOffers.matchFulfilled, setMplOfferStatusesFromRankingOffers);
  },
});

export const {
  setScenario,
  setLoading,
  setError,
  setMplPreApprovalOfferStatus,
  setDisplayedScreen,
  clearScenario,
  currentStepClosed,
} = scenarioSlice.actions;
